import React from "react";
import PropTypes from "prop-types";
import { FileText, Calendar, Eye } from "lucide-react";
import { formatDate, USDollar } from "../../../utilities/functions/Helpers";

const invoiceCardShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  issuedDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  dueDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  amountDue: PropTypes.number,
  totalAmountPaid: PropTypes.number,
  status: PropTypes.string,
  reservation: PropTypes.shape({
    totalPrice: PropTypes.number,
  }),
});

const InvoiceCard = ({ invoice, onView }) => {
  const {
    issuedDate,
    dueDate,
    amountDue,
    totalAmountPaid,
    status,
    reservation,
  } = invoice;

  const totalAmount = reservation?.totalPrice || amountDue + totalAmountPaid || 0;

  const isPaid = status === "PAID";
  const isOverdue = new Date(dueDate) < new Date() && !isPaid;

  const getStatusColor = (status) => {
    const lowerStatus = status?.toLowerCase();
    if (lowerStatus?.includes("paid"))
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    if (lowerStatus?.includes("pending"))
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
    if (lowerStatus?.includes("overdue") || isOverdue)
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
    return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden flex flex-col h-full">
      {/* Card Header */}
      <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-start bg-slate-50/50 dark:bg-slate-800/50">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-lg text-blue-600 dark:text-blue-400">
            <FileText size={20} />
          </div>
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium uppercase tracking-wider">
              Invoice
            </p>
            <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
              #{invoice.id}
            </p>
          </div>
        </div>
        <span
          className={`px-2 py-1 rounded-md text-xs font-bold uppercase ${getStatusColor(status)}`}
        >
          {status}
        </span>
      </div>

      {/* Card Body */}
      <div className="p-5 flex-1 space-y-4">
        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
              Issued Date
            </p>
            <div className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
              <Calendar size={14} className="text-slate-400" />
              <span className="text-sm font-medium">
                {formatDate(issuedDate)}
              </span>
            </div>
          </div>
          <div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">
              Due Date
            </p>
            <div className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
              <Calendar size={14} className="text-slate-400" />
              <span
                className={`text-sm font-medium ${isOverdue ? "text-red-500" : ""}`}
              >
                {formatDate(dueDate)}
              </span>
            </div>
          </div>
        </div>

        <div className="h-px bg-slate-100 dark:bg-slate-800" />

        {/* Financials */}
        <div className="space-y-2">
          <div className="flex justify-between items-center text-sm">
            <span className="text-slate-500 dark:text-slate-400">
              Total Amount
            </span>
            <span className="font-semibold text-slate-700 dark:text-slate-200">
              {USDollar.format(totalAmount)}
            </span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-slate-500 dark:text-slate-400">
              Amount Paid
            </span>
            <span className="font-medium text-green-600 dark:text-green-400">
              {USDollar.format(totalAmountPaid)}
            </span>
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-slate-100 dark:border-slate-800">
            <span className="font-bold text-slate-700 dark:text-white">
              Amount Due
            </span>
            <span className="font-bold text-blue-600 dark:text-blue-400 text-lg">
              {USDollar.format(amountDue)}
            </span>
          </div>
        </div>
      </div>

      {/* Card Footer */}
      <div className="p-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/30">
        <button
          onClick={() => onView(invoice)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 hover:text-blue-600 dark:hover:text-blue-400 transition-colors shadow-sm"
        >
          <Eye size={16} /> View Details & Print
        </button>
      </div>
    </div>
  );
};

InvoiceCard.propTypes = {
  invoice: invoiceCardShape.isRequired,
  onView: PropTypes.func.isRequired,
};

export default InvoiceCard;
